export type SortDir = 'asc' | 'desc';

/**
 * Self-contained cursor-based pagination state shared by all client-stats section
 * components. Has no Angular dependency — pure TypeScript.
 */
export class CursorPaginationState {
  totalCnt: number | null = null;
  maxId:    number | null = null;
  minId:    number | null = null;

  private readonly cache = new Map<string, number>();

  readonly bufferSize = 10;
  readonly padding    = 0.25;

  cacheKey(dir: SortDir, blockStart: number): string {
    return `${dir}:${blockStart}`;
  }

  /**
   * Resolve the starting record-ID cursor for a logical scroll block.
   * 1. Cache hit  → return cached value.
   * 2. blockStart = 0 → seed from meta (maxId for DESC, minId/0 for ASC).
   * 3. Otherwise fall back to the previous block's cached cursor, or recurse to (2).
   */
  resolveStartId(blockStart: number, dir: SortDir, blockSize: number): number | null {
    const cached = this.cache.get(this.cacheKey(dir, blockStart));
    if (typeof cached === 'number') return cached;

    if (blockStart === 0)
      return dir === 'desc' ? (this.maxId ?? null) : (this.minId ?? 0);

    const prev = this.cache.get(this.cacheKey(dir, blockStart - blockSize));
    return typeof prev === 'number' ? prev : this.resolveStartId(0, dir, blockSize);
  }

  /** Cache the cursor for the block that follows the one just received. */
  cacheNextBlock(dir: SortDir, blockStart: number, blockSize: number, lastId: number): void {
    const next = dir === 'asc' ? lastId + 1 : lastId - 1;
    this.cache.set(this.cacheKey(dir, blockStart + blockSize), next);
  }

  /** Update min/max/total metadata from a chunk response. */
  updateMeta(totalCnt: number, minId: number, maxId: number): void {
    this.totalCnt = totalCnt;
    this.minId    = minId;
    this.maxId    = maxId;
  }

  /** Seed the blockStart=0 cursor for the given sort direction. */
  seedForSort(dir: SortDir): void {
    if (dir === 'desc' && this.maxId != null)
      this.cache.set(this.cacheKey('desc', 0), this.maxId);
    if (dir === 'asc')
      this.cache.set(this.cacheKey('asc', 0), this.minId ?? 0);
  }

  clear(): void { this.cache.clear(); }

  /**
   * Full reset: clears the cache AND zeroes the meta. Use when the underlying
   * dataset changes (e.g. new search criteria), so the next chunk request
   * starts from a clean cursor rather than reusing stale minId/maxId from the
   * previous dataset.
   */
  reset(): void {
    this.cache.clear();
    this.totalCnt = null;
    this.minId    = null;
    this.maxId    = null;
  }
}
